import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/states';

/**
 * "No rows" and "the query failed" must never render the same.
 *
 * Every list here is tenant-scoped under `tenant_isolation` RLS — ADR-0002
 * and ADR-0004 both say so of their own tables — so an unbound read returns
 * **zero rows rather than an error**. `Pages/Fleet/components/PanelResult.tsx`
 * names the production bug this already caused once: an empty result rendered
 * as "nothing configured" when the query was actually broken, and somebody
 * spent an evening re-doing work that already existed. This is that same
 * component, kept local rather than imported, because its copy is
 * `integrations.states.*` and Fleet's is `fleet.states.*` — the shape is
 * shared, the strings are not.
 *
 * Four distinct outcomes:
 *  - **loading** — `LoadingState`.
 *  - **error** — a bordered, tinted `role="alert"` box with the server's own
 *    words and a Retry. Never centred grey text, so it cannot be mistaken for
 *    a list that happens to be short.
 *  - **empty, filtered** — rows exist, these filters exclude them.
 *  - **empty, unfiltered** — genuinely nothing configured yet, with the
 *    action that fixes it, when there is one.
 */
export interface PanelResultProps {
  loading: boolean;
  error: string | null;
  count: number;
  onRetry: () => void;
  filtered?: boolean;
  onClearFilters?: () => void;
  emptyTitle: string;
  emptyHint?: string;
  emptyAction?: ReactNode;
  children: ReactNode;
}

export function PanelResult({
  loading,
  error,
  count,
  onRetry,
  filtered,
  onClearFilters,
  emptyTitle,
  emptyHint,
  emptyAction,
  children,
}: PanelResultProps) {
  const { t } = useTranslation();

  if (loading) return <LoadingState />;

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50/70 p-6 text-center dark:border-red-900 dark:bg-red-950/30"
      >
        <div className="grid place-items-center gap-2">
          <Icon as={TriangleAlert} size="xl" className="text-red-600 dark:text-red-400" />
          <p className="text-sm font-medium">{t('integrations.states.loadFailed')}</p>
          <p className="max-w-lg text-sm text-ink-muted">{error}</p>
          <p className="max-w-lg text-xs text-ink-muted">{t('integrations.states.loadFailedHint')}</p>
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
            <Icon as={RefreshCw} />
            {t('common.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  if (count === 0 && filtered) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={SearchX} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{t('integrations.states.noMatches')}</p>
        <p className="max-w-sm text-sm text-ink-muted">{t('integrations.states.noMatchesHint')}</p>
        {onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            {t('integrations.states.clearFilters')}
          </Button>
        )}
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={Inbox} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{emptyTitle}</p>
        {emptyHint && <p className="max-w-sm text-sm text-ink-muted">{emptyHint}</p>}
        {emptyAction}
      </div>
    );
  }

  return <>{children}</>;
}

export default PanelResult;
